import {
  AfterViewInit,
  Component,
  ElementRef,
  forwardRef,
  HostBinding,
  Input,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import intlTelInput, { Iti, Iso2 } from 'intl-tel-input';
import {
  PHONE_INPUT_COUNTRY_ORDER,
  PHONE_INPUT_FRENCH_TRANSLATIONS,
  PHONE_INPUT_INITIAL_COUNTRY,
  PHONE_INPUT_LOAD_UTILS,
} from '../../config/phone-input.config';
import { getDialCodeFromIntlInput } from '../../utils/phone.utils';

@Component({
  selector: 'app-phone-input',
  standalone: true,
  templateUrl: './phone-input.component.html',
  styleUrls: ['./phone-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PhoneInputComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => PhoneInputComponent),
      multi: true,
    },
  ],
})
export class PhoneInputComponent
  implements AfterViewInit, OnDestroy, ControlValueAccessor, Validator
{
  @ViewChild('telInput') telInputRef!: ElementRef<HTMLInputElement>;
  @Input() inputId = 'phone';
  @Input() placeholder = 'Numéro de téléphone';
  @Input() invalid = false;

  @HostBinding('class.phone-input--invalid')
  get showInvalid(): boolean {
    return this.invalid;
  }

  private iti?: Iti;
  private pendingValue: string | null = null;
  private countryButton?: HTMLElement | null;
  private onChange: (value: string) => void = () => undefined;
  private onTouched: () => void = () => undefined;
  private onValidatorChange: () => void = () => undefined;

  ngAfterViewInit(): void {
    const input = this.telInputRef.nativeElement;
    input.placeholder = this.placeholder;

    this.iti = intlTelInput(input, {
      initialCountry: PHONE_INPUT_INITIAL_COUNTRY,
      countryOrder: [...PHONE_INPUT_COUNTRY_ORDER],
      separateDialCode: true,
      formatAsYouType: true,
      loadUtils: PHONE_INPUT_LOAD_UTILS,
      containerClass: 'phone-input__iti',
      uiTranslations: PHONE_INPUT_FRENCH_TRANSLATIONS,
      countrySearch: true,
      countrySelectorMode: 'DROPDOWN',
      dropdownParent: document.body,
      showFlags: true,
    });

    input.addEventListener('input', () => this.handleChange());
    input.addEventListener('countrychange', () => this.onCountryChange());
    input.addEventListener('blur', () => this.onTouched());
    document.addEventListener('click', this.onDocumentCountryClick, true);
    document.addEventListener('input', this.onDocumentSearchInput, true);

    this.countryButton = input.closest('.iti')?.querySelector('.iti__selected-country');
    this.countryButton?.addEventListener('click', this.onCountryButtonCapture, true);

    if (this.pendingValue !== null) {
      this.applyValue(this.pendingValue);
      this.pendingValue = null;
    }
  }

  ngOnDestroy(): void {
    this.countryButton?.removeEventListener('click', this.onCountryButtonCapture, true);
    document.removeEventListener('click', this.onDocumentCountryClick, true);
    document.removeEventListener('input', this.onDocumentSearchInput, true);
    this.iti?.destroy();
  }

  getInstance(): Iti | undefined {
    return this.iti;
  }

  getDialCode(): string {
    return getDialCodeFromIntlInput(this.iti);
  }

  writeValue(value: string | null): void {
    if (!this.iti) {
      this.pendingValue = value;
      return;
    }

    this.applyValue(value);
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.iti?.setDisabled(isDisabled);
  }

  validate(control: AbstractControl): ValidationErrors | null {
    if (!this.iti) {
      return null;
    }

    const value = ((control.value as string | null) ?? '').trim();
    if (!value) {
      return null;
    }

    try {
      if (this.iti.isValidNumber() === false) {
        return { invalidPhone: this.iti.getValidationError() };
      }
    } catch {
      return null;
    }

    return null;
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  private onCountryChange(): void {
    this.iti?.closeCountrySelector();
    this.handleChange();
  }

  /** Library only opens the list; if it stays open, a second click must close it. */
  private readonly onCountryButtonCapture = (event: Event): void => {
    if (!this.isCountrySelectorOpen()) {
      return;
    }

    event.stopImmediatePropagation();
    this.iti?.closeCountrySelector();
  };

  private isCountrySelectorOpen(): boolean {
    const instanceId = this.telInputRef.nativeElement.dataset['intlTelInputId'];
    const selector =
      (instanceId ? document.getElementById(`iti-${instanceId}__country-selector`) : null) ??
      this.telInputRef.nativeElement.closest('.iti')?.querySelector('.iti__country-selector');

    return !!selector && !selector.classList.contains('iti__hide');
  }

  /**
   * intl-tel-input binds list/search listeners with AbortSignal and removes them
   * on close. After the first selection those listeners often never return, so
   * we keep a document-level fallback for this instance's dropdown.
   */
  private readonly onDocumentCountryClick = (event: Event): void => {
    const item = (event.target as HTMLElement | null)?.closest('.iti__country') as HTMLElement | null;
    if (!item || !this.isOwnCountryList(item) || !this.iti) {
      return;
    }

    const iso2 = item.dataset['iso2'];
    if (!iso2) {
      return;
    }

    this.iti.setSelectedCountry(iso2 as Iso2);
    this.iti.closeCountrySelector();
  };

  private readonly onDocumentSearchInput = (event: Event): void => {
    const input = event.target as HTMLInputElement | null;
    if (!input?.classList.contains('iti__search-input') || !this.isOwnSearchInput(input)) {
      return;
    }

    const query = input.value.trim().toLowerCase();
    const list = this.getOwnCountryList();
    list?.querySelectorAll<HTMLElement>('.iti__country').forEach((item) => {
      const haystack = (item.textContent ?? '').toLowerCase();
      item.hidden = query.length > 0 && !haystack.includes(query);
    });
  };

  private isOwnCountryList(item: HTMLElement): boolean {
    const list = item.closest('.iti__country-list');
    return !!list && list.id === this.countryListId();
  }

  private isOwnSearchInput(input: HTMLInputElement): boolean {
    return input.id === this.searchInputId();
  }

  private getOwnCountryList(): HTMLElement | null {
    const id = this.countryListId();
    return id ? document.getElementById(id) : null;
  }

  private countryListId(): string | null {
    const instanceId = this.telInputRef.nativeElement.dataset['intlTelInputId'];
    return instanceId ? `iti-${instanceId}__country-listbox` : null;
  }

  private searchInputId(): string | null {
    const instanceId = this.telInputRef.nativeElement.dataset['intlTelInputId'];
    return instanceId ? `iti-${instanceId}__search-input` : null;
  }

  private handleChange(): void {
    let value = this.telInputRef.nativeElement.value;
    try {
      value = this.iti?.getNumber() ?? value;
    } catch {
      // Utils may not be loaded yet.
    }
    this.onChange(value);
    this.onValidatorChange();
  }

  private applyValue(value: string | null): void {
    if (!this.iti) {
      return;
    }

    if (value) {
      this.iti.setNumber(value);
      return;
    }

    this.telInputRef.nativeElement.value = '';
  }
}
